import { CloseRounded } from '@mui/icons-material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DEFAULT_VIRTUAL_FENCE_COLOR } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntities as getCircularEntities } from 'app/entities/virtual-fence-circle/virtual-fence-circle.reducer';
import { getEntities as getPolygonEntities } from 'app/entities/virtual-fence-polygon/virtual-fence-polygon.reducer';
import { IVirtualFenceCircle } from 'app/shared/model/virtual-fence-circle.model';
import { IVirtualFencePolygon } from 'app/shared/model/virtual-fence-polygon.model';
import { Loading } from 'app/shared/util/loading';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';

interface FenceEntry {
  id: number;
  name: string;
  color: string | null | undefined;
  geometry: string; 
  type: 'CIRCLE' | 'POLYGON';
}

interface Props {
  handleSave: (circleIds: number[], polygonIds: number[]) => void;
  toggleModal: () => void;
  initialCircleFenceIds: number[];
  initialPolygonFenceIds: number[];
  open: boolean;
  companyId?: number;
}

export const TruckGroupVirtualFenceSelectionModal = ({
  handleSave,
  toggleModal,
  initialCircleFenceIds,
  initialPolygonFenceIds,
  open,
  companyId,
}: Props) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const circleList = useAppSelector(state => state.virtualFenceCircle.entities) as IVirtualFenceCircle[];
  const polygonList = useAppSelector(state => state.virtualFencePolygon.entities) as IVirtualFencePolygon[];
  const circleLoading = useAppSelector(state => state.virtualFenceCircle.loading);
  const polygonLoading = useAppSelector(state => state.virtualFencePolygon.loading);
  const loading = circleLoading || polygonLoading;

  const [selectedCircles, setSelectedCircles]   = useState<Set<number>>(new Set(initialCircleFenceIds));
  const [selectedPolygons, setSelectedPolygons] = useState<Set<number>>(new Set(initialPolygonFenceIds));

  const [filterValue, setFilterValue]             = useState('');
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');
  const [filtering, setFiltering]                 = useState(false);

  const debouncedSetFilter = useCallback(
    debounce((v: string) => { setDebouncedFilterValue(v); setFiltering(false); }, 500),
    []
  );

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setFilterValue(v);
    if (v) { setFiltering(true); debouncedSetFilter(v); }
    else   { setDebouncedFilterValue(''); }
  };

  useEffect(() => {
    dispatch(getCircularEntities({ page: 0, size: 999, sort: 'name,asc', companyId }));
    dispatch(getPolygonEntities({ page: 0, size: 999, sort: 'name,asc', companyId }));
  }, [companyId]);

  useEffect(() => {
    setSelectedCircles(new Set(initialCircleFenceIds));
    setSelectedPolygons(new Set(initialPolygonFenceIds));
    setFilterValue('');
    setDebouncedFilterValue('');
    setFiltering(false);
  }, [open, initialCircleFenceIds.join(','), initialPolygonFenceIds.join(',')]);

  const allFences: FenceEntry[] = useMemo(() => {
    const circles: FenceEntry[] = (circleList || []).map(f => ({
      id: f.id,
      name: f.name ?? '',
      color: f.color,
      geometry: f.radius != null ? `${f.radius} m` : '',
      type: 'CIRCLE' as const,
    }));
    const polygons: FenceEntry[] = (polygonList || []).map(f => ({
      id: f.id,
      name: f.name ?? '',
      color: f.color,
      geometry: f.points?.length ? `${f.points.length} pontos` : '',
      type: 'POLYGON' as const,
    }));
    const combined = [...circles, ...polygons];

    const allInitial = new Set([...initialCircleFenceIds, ...initialPolygonFenceIds]);
    return combined.sort((a, b) => {
      const aSelected = allInitial.has(a.id) ? 0 : 1;
      const bSelected = allInitial.has(b.id) ? 0 : 1;
      if (aSelected !== bSelected) return aSelected - bSelected;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [circleList, polygonList, initialCircleFenceIds.join(','), initialPolygonFenceIds.join(',')]);

  const filteredFences = useMemo(() => {
    if (!debouncedFilterValue) return allFences;
    return allFences.filter(f => f.name.toLowerCase().includes(debouncedFilterValue.toLowerCase()));
  }, [allFences, debouncedFilterValue]);

  const isSelected = (f: FenceEntry) =>
    f.type === 'CIRCLE' ? selectedCircles.has(f.id) : selectedPolygons.has(f.id);

  const totalSelected = selectedCircles.size + selectedPolygons.size;
  const allFilteredSelected = filteredFences.length > 0 && filteredFences.every(isSelected);
  const someFilteredSelected = filteredFences.some(isSelected);

  const handleToggle = (f: FenceEntry) => {
    if (f.type === 'CIRCLE') {
      setSelectedCircles(prev => { const n = new Set(prev); n.has(f.id) ? n.delete(f.id) : n.add(f.id); return n; });
    } else {
      setSelectedPolygons(prev => { const n = new Set(prev); n.has(f.id) ? n.delete(f.id) : n.add(f.id); return n; });
    }
  };

  const handleSelectAll = () => {
    const allSel = filteredFences.every(isSelected);
    setSelectedCircles(prev => {
      const n = new Set(prev);
      filteredFences.filter(f => f.type === 'CIRCLE').forEach(f => allSel ? n.delete(f.id) : n.add(f.id));
      return n;
    });
    setSelectedPolygons(prev => {
      const n = new Set(prev);
      filteredFences.filter(f => f.type === 'POLYGON').forEach(f => allSel ? n.delete(f.id) : n.add(f.id));
      return n;
    });
  };

  const handleSaveClick = () => {
    handleSave(Array.from(selectedCircles), Array.from(selectedPolygons));
    toggleModal();
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={toggleModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
        sx={{ boxShadow: theme.shadows[24] }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            Cercas Virtuais
            <Typography component="span" variant="caption" sx={{ opacity: 0.7, fontWeight: 400 }}>
              ({totalSelected}/{filteredFences.length} selecionadas)
            </Typography>
          </Typography>
          <IconButton onClick={toggleModal}>
            <CloseRounded color="secondary" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {!loading && allFences.length > 0 && (
            <>
              <Box display="flex" alignItems="center" mt={1} mb={2}>
                <TextField
                  fullWidth size="small"
                  value={filterValue}
                  onChange={handleFilterChange}
                  placeholder="Buscar por nome"
                  InputProps={{
                    sx: { borderRadius: '8px', p: '0 8px 2px 0' },
                    endAdornment: (
                      <InputAdornment position="end">
                        {!filterValue ? (
                          <SearchOutlinedIcon color="action" />
                        ) : (
                          <IconButton size="small" onClick={() => { setFilterValue(''); setDebouncedFilterValue(''); }}>
                            <CloseOutlinedIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected && !allFilteredSelected}
                      onChange={handleSelectAll}
                      color="secondary"
                    />
                  }
                  label="Selecionar Todas"
                />
              </Box>
            </>
          )}

          <Box sx={{ mt: 1 }}>
            {filtering || loading ? (
              <Loading />
            ) : filteredFences.length > 0 ? (
              filteredFences.map(fence => (
                <Box
                  key={`${fence.type}-${fence.id}`}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => handleToggle(fence)}
                  sx={{
                    cursor: 'pointer',
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderBottomColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.06)
                        : 'divider',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Checkbox checked={isSelected(fence)} color="secondary" />
                    <FontAwesomeIcon
                      icon={faCircle}
                      color={fence.color ?? DEFAULT_VIRTUAL_FENCE_COLOR}
                      style={{ flexShrink: 0 }}
                    />
                    <Box>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <Typography variant="subtitle2">{fence.name}</Typography>
                        {/* Type chip — distinguishes circular from polygon */}
                        <Chip
                          label={fence.type === 'POLYGON' ? 'Poligonal' : 'Circular'}
                          size="small"
                          variant="outlined"
                          color={fence.type === 'POLYGON' ? 'success' : 'info'}
                          sx={{ fontSize: '0.65rem', height: 16, borderRadius: '4px', px: 0.25 }}
                        />
                      </Box>
                      {fence.geometry && (
                        <Typography variant="caption" color="text.secondary">
                          {fence.geometry}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box display="flex" justifyContent="center" mt={2} mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma cerca encontrada.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: '8px 24px', my: 2 }}>
          <Button onClick={toggleModal} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            Cancelar
          </Button>
          <Button onClick={handleSaveClick} disableRipple variant="contained" color="secondary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
